import type {TransceiverConfig} from '@pexip/peer-connection';
import type {
    Signal,
    SignalOptions,
    GenericSignalOptions,
    ReplaySignalOptions,
    BehaviorSignalOptions,
    BatchedSignalOptions,
} from '@pexip/signal';
import {combine, createSignal} from '@pexip/signal';
import {createScheduler} from '@pexip/utils';
import {createStatsSignals} from '@pexip/peer-connection-stats';

import type {
    IceCandidate,
    CallSignals,
    InfinityCallSignalsOptional as CallSignalsOptional,
    InfinitySignals,
    InfinitySignalsOptional,
    PresentationEvent,
    InfinityEventSignalsOptional,
    EventSignals,
    RegistrationSignals,
    RTCParticipantEvent,
    CurrentServiceType,
    Client,
    Participant,
    MediaType,
    ConferenceStateEvent,
    ConferenceStatus,
    MainStatsSignals,
    NormalizedPresentationEvent,
    AllowMediaChangeOfDirection,
} from './types';
import {CallType} from './types';
import {ClientCallType} from './clientCallType';
import {assertParticipantID} from './typeGuards';
import {logger} from './logger';

/**
 * Creates a serial queue that ensures async operations run one at a time.
 * Each enqueued operation waits for the previous one to settle before starting.
 */
export const createSerialQueue = () => {
    let pending: Promise<unknown> | undefined;

    const enqueue = <T>(fn: () => Promise<T>): Promise<T> => {
        const op = (pending ?? Promise.resolve()).then(fn, fn);
        pending = op;
        return op;
    };

    const reset = () => {
        pending = undefined;
    };

    return {enqueue, reset};
};

/**
 * Create a general signal with consistent scoped name
 *
 * @param name - Signal name
 * @param scope - The scope of the signal for better logging
 * @param variant - The variant of the signal @see Signal @defaultValue 'generic'
 */
function createInfinitySignal<T = undefined>(
    options: GenericSignalOptions,
    scope: string,
): Signal<T>;
function createInfinitySignal<T = undefined>(
    options: ReplaySignalOptions,
    scope: string,
): Signal<T>;
function createInfinitySignal<T = undefined>(
    options: BehaviorSignalOptions,
    scope: string,
): Signal<T>;
function createInfinitySignal<T = undefined>(
    options: BatchedSignalOptions,
    scope: string,
): Signal<T, T[]>;
function createInfinitySignal<T = undefined>(
    options: SignalOptions,
    scope: string,
): Signal<T, T>;
function createInfinitySignal<T = undefined>(
    options: SignalOptions,
    scope = '',
) {
    switch (options.variant) {
        case 'batched': {
            return createSignal<T>({
                ...options,
                name: `infinity/${scope}:${options.name}`,
                allowEmittingWithoutObserver: allowEmittingWithoutObserver(
                    options.name ?? '',
                ),
            });
        }
        case 'replay': {
            return createSignal<T>({
                ...options,
                name: `infinity/${scope}:${options.name}`,
                allowEmittingWithoutObserver: allowEmittingWithoutObserver(
                    options.name ?? '',
                ),
            });
        }
        default: {
            return createSignal<T>({
                ...options,
                name: `infinity/${scope}:${options.name}`,
                allowEmittingWithoutObserver: allowEmittingWithoutObserver(
                    options.name ?? '',
                ),
            });
        }
    }
}

const allowEmittingWithoutObserver = (signal: string) =>
    new Set([
        'onRtcStats',
        'onCallQualityStats',
        'onCallQuality',
        'onRequestedLayout',
        'onStage',
        'onPresentationFrame',
    ]).has(signal);

const REQUIRED_INFINITY_SIGNAL_KEYS = [
    'onAnswer',
    'onApplicationMessage',
    'onAuthenticatedWithConference',
    'onTokenRefreshed',
    'onTokenExpired',
    'onBreakoutBegin',
    'onBreakoutEnd',
    'onBreakoutRefer',
    'onCallConnected',
    'onCallDisconnected',
    'onCancelTransfer',
    'onConferenceStatus',
    'onConnected',
    'onDisconnected',
    'onError',
    'onExtension',
    'onFailedRequest',
    'onFecc',
    'onIceCandidate',
    'onIdp',
    'onLayoutOverlayTextEnabled',
    'onLayoutUpdate',
    'onLiveCaptions',
    'onMe',
    'onMessage',
    'onMyselfMuted',
    'onNewOffer',
    'onParticipantJoined',
    'onParticipantLeft',
    'onParticipantUpdated',
    'onParticipants',
    'onPeerDisconnect',
    'onPinRequired',
    'onPresentationAnswer',
    'onRaiseHand',
    'onRedirect',
    'onRequestedLayout',
    'onRetryQueueFlushed',
    'onServiceType',
    'onSplashScreen',
    'onStage',
    'onTransfer',
    'onUpdateSdp',
    'onDisplayNameRequired',
] as const;

/**
 * Create and return all required and optional (if specified with `more`),
 * signals for infinity client to work
 *
 * @param scope - any scope prefix for the generated signal name, @see Signal
 * @param more - Keys from `InfinitySignalsOptional`, @see InfinitySignalsOptional
 *
 * The following signals created by default
 * - 'onConnected',
 * - 'onAnswer',
 *
 * @see REQUIRED_INFINITY_SIGNAL_KEYS
 */
export const createInfinityClientSignals = <
    K extends keyof InfinitySignalsOptional,
>(
    more: K[],
    {
        scope = '',
        batchScheduleTimeoutMS,
        batchBufferSize,
    }: {
        scope?: string;
        batchScheduleTimeoutMS: number;
        batchBufferSize: number;
    },
) => {
    const signalScope = scope && [scope, ':'].join('');
    type SignalKeys =
        | (typeof more)[number]
        | (typeof REQUIRED_INFINITY_SIGNAL_KEYS)[number]
        | 'cancel';
    const schedulers: ReturnType<typeof createScheduler>[] = [];
    return [...REQUIRED_INFINITY_SIGNAL_KEYS, ...more].reduce(
        (signals, key) => {
            switch (key) {
                case 'onParticipantLeft':
                case 'onParticipantUpdated':
                case 'onParticipantJoined': {
                    const scheduler = createScheduler({
                        delay: batchScheduleTimeoutMS,
                        priority: 'user-blocking', // Bump the priority to minimize the delay
                    });
                    signals[key] = createInfinitySignal<
                        InfinitySignals[typeof key] extends Signal<
                            infer S,
                            unknown[]
                        >
                            ? S
                            : never
                    >(
                        {
                            name: key,
                            variant: 'batched',
                            bufferSize: batchBufferSize,
                            emitImmediatelyWhenFull: true,
                            schedule: scheduler.schedule,
                        },
                        signalScope,
                    );
                    schedulers.push(scheduler);
                    break;
                }
                default: {
                    // @ts-expect-error
                    signals[key] = createInfinitySignal<
                        InfinitySignals[typeof key] extends Signal<infer S>
                            ? S
                            : never
                    >({name: key, variant: 'generic'}, signalScope);
                    break;
                }
            }
            return signals;
        },
        {
            cancel() {
                logger.debug(
                    {schedulers},
                    'Cancelling all scheduled batched signals',
                );
                this.onParticipantJoined.clearBuffers();
                this.onParticipantUpdated.clearBuffers();
                this.onParticipantLeft.clearBuffers();
                for (const scheduler of schedulers) {
                    scheduler.cancel();
                }
            },
        } as Pick<Required<InfinitySignals>, SignalKeys>,
    );
};

const REQUIRED_CALL_SIGNAL_KEYS = [
    'onCallConnected',
    'onRemoteStream',
    'onRemotePresentationStream',
    'onPresentationConnectionChange',
    'onRtcStats',
    'onCallQualityStats',
    'onCallQuality',
    'onSecureCheckCode',
    'onReconnecting',
    'onReconnected',
] as const;

/**
 * Create and return all required and optional (if specified with `more`),
 * signals for call to work
 *
 * @param scope - any scope prefix for the generated signal name, @see Signal
 * @param more - Keys from `CallSignalsOptional`, @see CallSignalsOptional
 *
 * The following signals created by default
 * - 'onRemoteStream',
 *
 * @see REQUIRED_CALL_SIGNAL_KEYS
 */
export const createCallSignals = <K extends keyof CallSignalsOptional>(
    more: K[],
    scope = '',
) => {
    const callScope = 'call';
    const signalScope = scope && [scope, ':', callScope, ':'].join('');
    type SignalKeys =
        (typeof more)[number] | (typeof REQUIRED_CALL_SIGNAL_KEYS)[number];
    return [...REQUIRED_CALL_SIGNAL_KEYS, ...more].reduce(
        (signals, key) => {
            // @ts-expect-error
            signals[key] = createInfinitySignal<
                CallSignals[typeof key] extends Signal<infer S> ? S : never
            >({name: key, variant: 'generic'}, signalScope);
            return signals;
        },
        {} as Pick<Required<CallSignals>, SignalKeys>,
    );
};

const REQUIRED_REGISTRATION_SIGNAL_KEYS = [
    'onIncoming',
    'onIncomingCancelled',
] as const;

/**
 * Create and return all required signals for registration to work
 *
 * @param scope - any scope prefix for the generated signal name, @see Signal
 *
 * @see REQUIRED_REGISTRATION_SIGNAL_KEYS
 *
 * @example
 * ```ts
 * const registrationSignals = createRegistrationSignals('my-scope');
 * registrationSignals.onIncoming.add(incoming => { });
 * ```
 */
export const createRegistrationSignals = (scope = '') => {
    const registrationScope = 'registration';
    const signalScope = scope && [scope, ':', registrationScope, ':'].join('');
    return REQUIRED_REGISTRATION_SIGNAL_KEYS.reduce(
        (signals, key) => {
            // @ts-expect-error
            signals[key] = createInfinitySignal<
                RegistrationSignals[typeof key] extends Signal<infer S>
                    ? S
                    : never
            >({name: key, variant: 'generic'}, signalScope);
            return signals;
        },
        {} as Pick<
            Required<RegistrationSignals>,
            (typeof REQUIRED_REGISTRATION_SIGNAL_KEYS)[number]
        >,
    );
};

const REQUIRED_EVENT_SIGNAL_KEYS = [
    'onConnected',
    'onError',
    'onCallDisconnected',
    'onPresentationStart',
    'onPresentationStop',
    'onPresentationEnded',
    'onPresentationFrame',
    'onParticipantCreate',
    'onParticipantUpdate',
    'onParticipantDelete',
    'onMessage',
    'onConferenceUpdate',
    'onStageUpdate',
    'onLayoutUpdate',
    'onDisconnect',
    'onParticipantSyncBegin',
    'onParticipantSyncEnd',
    'onRefer',
    'onCancelRefer',
    'onHold',
    'onFecc',
    'onRefreshToken',
    'onLiveCaptions',
    'onPeerDisconnect',
    'onNewOffer',
    'onUpdateSdp',
    'onNewCandidate',
    'onSplashScreen',
    'onBreakoutBegin',
    'onBreakoutEvent',
    'onBreakoutEnd',
    'onBreakoutRefer',
] as const;
/**
 * Create and return all required and optional (if specified with `more`),
 * signals for call to work
 *
 * @param scope - any scope prefix for the generated signal name, @see Signal
 * @param more - Keys from `InfinityEventSignalsOptional`, @see InfinityEventSignalsOptional
 *
 * The following signals created by default
 * - 'onRemoteStream',
 *
 * @see REQUIRED_EVENT_SIGNAL_KEYS
 */
export const createEventSignals = <
    K extends keyof InfinityEventSignalsOptional,
>(
    more: K[],
    scope = '',
) => {
    const callScope = 'events';
    const signalScope = scope && [scope, ':', callScope, ':'].join('');
    type SignalKeys =
        (typeof more)[number] | (typeof REQUIRED_EVENT_SIGNAL_KEYS)[number];
    return [...REQUIRED_EVENT_SIGNAL_KEYS, ...more].reduce(
        (signals, key) => {
            // @ts-expect-error
            signals[key] = createInfinitySignal<
                EventSignals[typeof key] extends Signal<infer S> ? S : never
            >({name: key, variant: 'generic'}, signalScope);
            return signals;
        },
        {} as Pick<Required<EventSignals>, SignalKeys>,
    );
};

/**
 * Convert `RTCIceCandidate` into Infinity ICE candidate data structure.
 */
export const toIceCandidate = (
    iceCandidate: RTCIceCandidate | null,
): IceCandidate => ({
    candidate: iceCandidate?.candidate ?? '',
    mid: iceCandidate?.sdpMid ?? '0',
    ...(iceCandidate?.usernameFragment && {
        ufrag: iceCandidate.usernameFragment,
    }),
});

export const getPresenter = (presentationEvent: PresentationEvent) => {
    const {presenter_name, presenter_uri} = presentationEvent;
    if (presenter_name && presenter_uri) {
        return `${presenter_name} <${presenter_uri}>`;
    }

    return presentationEvent.presenter_uri ?? '';
};

export const normalizePresentationEvent = (event: PresentationEvent) => ({
    presenterDisplayName: getPresenter(event),
    presenterName: event.presenter_name,
    presenterUri: event.presenter_uri,
    presenterUuid: event.presenter_uuid,
});

/**
 * ```json
 * {"presenter_name": "", "presenter_uri": "", "presenter_uuid": ""}
 * ```
 * This is an artificial event send from mcu side which means that we only want
 * to stop current presentation if we are presenting and we shouldn't start
 * receiving presentation unless those properties are different than empty string.
 *
 * @returns if we should ignore receiving the presentation
 */
export const isPresentationRevoked = (event: NormalizedPresentationEvent) =>
    event.presenterName === '' &&
    event.presenterUri === '' &&
    event.presenterUuid === '';

const canControl = (participant: RTCParticipantEvent) => {
    switch (participant.service_type) {
        case 'conference':
        case 'lecture':
            return participant.role === 'chair';
        default:
            return false;
    }
};

const canChangeLayout = (participant: RTCParticipantEvent) => {
    switch (participant.service_type) {
        case 'conference':
        case 'lecture':
        case 'gateway':
            return participant.role === 'chair';
        default:
            return false;
    }
};

export const isWaiting = (serviceType: CurrentServiceType) =>
    serviceType === 'waiting_room';

export const isGateway = (serviceType: CurrentServiceType) =>
    serviceType === 'gateway';

export const isConnecting = (serviceType: CurrentServiceType) =>
    serviceType === 'connecting';

export const normalizeParticipant = (
    participant: RTCParticipantEvent,
): Participant => {
    assertParticipantID(participant.uuid);
    return {
        callType:
            participant.is_audio_only_call === 'YES'
                ? CallType.audio
                : participant.is_video_call === 'YES'
                  ? CallType.video
                  : CallType.api,
        canControl: canControl(participant),
        canChangeLayout: canChangeLayout(participant),
        canDisconnect: participant.disconnect_supported === 'YES',
        canFecc: participant.fecc_supported === 'YES',
        canMute: participant.mute_supported === 'YES',
        canRaiseHand: participant.service_type !== 'gateway',
        canSpotlight: participant.service_type !== 'gateway',
        canTransfer: participant.transfer_supported === 'YES',
        displayName:
            participant.display_name || participant?.uri?.replace('sip:', ''),
        overlayText: participant.overlay_text,
        handRaisedTime: participant.buzz_time,
        isCameraMuted: participant.is_video_muted,
        isConjoined: Boolean(participant.is_conjoined),
        isConnecting: isConnecting(participant.service_type),
        isEndpoint: participant?.uri?.includes('sip'),
        isExternal: participant.is_external,
        isIdpAuthenticated: participant.is_idp_authenticated,
        isGateway: isGateway(participant.service_type),
        isHost: participant.role === 'chair',
        isMainVideoDroppedOut: Boolean(participant.is_main_video_dropped_out),
        isMuted: participant.is_muted === 'YES',
        isClientMuted: Boolean(participant.is_client_muted),
        isPresenting: participant.is_presenting === 'YES',
        isSpotlight: participant.spotlight > 0,
        isStreaming: participant.is_streaming_conference,
        isTransferring: Boolean(participant.is_transferring),
        isVideoSilent: participant.is_video_silent,
        isWaiting: isWaiting(participant.service_type),
        needsPresentationInMix: Boolean(participant.needs_presentation_in_mix),
        hasMedia: participant.has_media,
        protocol: participant.protocol,
        raisedHand: participant.buzz_time > 0,
        role: participant.role,
        rxPresentation: participant.rx_presentation_policy === 'ALLOW',
        serviceType: participant.service_type,
        spotlightOrder: participant.spotlight,
        startAt: participant.start_time
            ? new Date(participant.start_time * 1000)
            : null,
        startTime: participant.start_time,
        supportsDirectChat: participant.supports_direct_chat,
        uri: participant.uri ?? '',
        uuid: participant.uuid,
        vendor: participant.vendor,
        canReceivePersonalMix: Boolean(participant.can_receive_personal_mix),
        receiveFromVideoMix: participant.receive_from_video_mix,
        rawData: participant,
    };
};

/**
 * Convert `MediaType/call_type` to `ClientCallType`
 * @see {@link https://docs.pexip.com/api_client/api_rest.htm#request_token}
 */
export const toClientCallType = (mediaType: MediaType): ClientCallType => {
    switch (mediaType) {
        case 'audio': {
            return ClientCallType.AudioSendRecv;
        }
        case 'video-only':
            return ClientCallType.AudioSendRecvVideoSendRecv;
        default: {
            return ClientCallType.AudioSendRecvVideoSendRecvPresentationSendRecv;
        }
    }
};

/**
 * The call type between the local and remote should be mutually inclusive
 */
export const matchCallType = (local: ClientCallType, remote: ClientCallType) =>
    local & remote;

/**
 *
 */
export const matchAllowMediaChangeOfDirection = (
    local: AllowMediaChangeOfDirection,
    callType: MediaType,
) => {
    switch (callType) {
        case 'audio': {
            return {
                video: false,
                audio: local.audio,
                presentation: false,
            };
        }
        case 'video-only': {
            return {
                video: local.video,
                audio: local.audio,
                presentation: false,
            };
        }
        case 'video': {
            return local;
        }
        default: {
            throw new Error('Unexpected call_type', {cause: callType});
        }
    }
};

export const normalizeConferenceState = (
    conferenceState: ConferenceStateEvent,
) => ({
    locked: conferenceState.locked,
    guestsMuted: conferenceState.guests_muted,
    allMuted: conferenceState.all_muted ?? false,
    started: conferenceState.started,
    liveCaptionsAvailable: conferenceState.live_captions_available ?? false,
    liveCaptionsAvailableLanguages:
        conferenceState.live_captions_available_languages ?? [],
    liveCaptionsSourceLanguages:
        conferenceState.live_captions_source_languages ?? [],
    breakoutRooms: conferenceState.breakout_rooms ?? false,
    classification:
        conferenceState.classification as ConferenceStatus['classification'],
    directMedia: conferenceState.direct_media ?? false,
    presentationAllowed: conferenceState.presentation_allowed ?? false,
    breakout: conferenceState.breakout ?? false,
    breakoutName: conferenceState.breakout_name,
    breakoutDescription: conferenceState.breakout_description,
    endTime: conferenceState.end_time,
    endAction: conferenceState.end_action,
    guestsAllowedToLeave: conferenceState.breakout_guests_allowed_to_leave,
    breakoutbuzz:
        conferenceState.breakoutbuzz as ConferenceStatus['breakoutbuzz'],
    guestsCanUnmute: conferenceState.guests_can_unmute,
    guestsCanPresent: conferenceState.guests_can_present,
    chatEnabled: conferenceState.chat_enabled,
    rawData: conferenceState,
});

export const captureNoRequestClient = () => {
    const errorMsg =
        'Attempted to create EventSource before RequestClient, or RequestClient has been already cleaned up. Aborting.';
    logger.warn(errorMsg);
    return;
};

export const isCriticalAction = (funcName: keyof Client) => {
    switch (funcName) {
        case 'ack':
        case 'call':
        case 'disconnect':
        case 'sendOffer':
            return true;
        default:
            return false;
    }
};

const toNumericalBool = (input: number): 1 | 0 => (input > 0 ? 1 : 0);

export const isControlOnly = (callType: ClientCallType) => !callType;
export const isReceivingAudio = (callType: ClientCallType): boolean =>
    Boolean(callType & ClientCallType.AudioRecv);
export const isReceivingVideo = (callType: ClientCallType): boolean =>
    Boolean(callType & ClientCallType.VideoRecv);
export const isReceivingPresentation = (callType: ClientCallType): boolean =>
    Boolean(callType & ClientCallType.PresentationRecv);
export const isReceivingAnyMedia = (callType: ClientCallType): boolean =>
    isReceivingAudio(callType) || isReceivingVideo(callType);
export const isSendingAudio = (callType: ClientCallType): boolean =>
    Boolean(callType & ClientCallType.AudioSend);
export const isSendingVideo = (callType: ClientCallType): boolean =>
    Boolean(callType & ClientCallType.VideoSend);
export const isSendingPresentation = (callType: ClientCallType): boolean =>
    Boolean(callType & ClientCallType.PresentationSend);
export const isSendingAnyMedia = (callType: ClientCallType): boolean =>
    isSendingAudio(callType) || isSendingVideo(callType);
export const isUnidirectionalAudio = (callType: ClientCallType) =>
    Boolean(
        toNumericalBool(callType & ClientCallType.AudioRecv) ^
        toNumericalBool(callType & ClientCallType.AudioSend),
    );
export const isUnidirectionalVideo = (callType: ClientCallType) =>
    Boolean(
        toNumericalBool(callType & ClientCallType.VideoRecv) ^
        toNumericalBool(callType & ClientCallType.VideoSend),
    );
export const isUnidirectional = (callType: ClientCallType) =>
    isUnidirectionalAudio(callType) || isUnidirectionalVideo(callType);

export const getBandwidth = (
    userPrefferredBandwidth?: number,
    maxBandwidth?: number,
) => {
    if (!maxBandwidth) {
        return userPrefferredBandwidth ?? 0;
    }

    if (!userPrefferredBandwidth) {
        return maxBandwidth ?? 0;
    }

    return Math.min(userPrefferredBandwidth, maxBandwidth);
};

export const isMainConfig = (config: TransceiverConfig) =>
    config.content === 'main';
export const isPresoConfig = (config: TransceiverConfig) =>
    config.content === 'slides';
export const isAudioConfig = (config: TransceiverConfig) =>
    config.kind === 'audio';
export const isVideoConfig = (config: TransceiverConfig) =>
    config.kind === 'video';
export const isPresoVideo = (config: TransceiverConfig) =>
    [isPresoConfig, isVideoConfig].every(fn => fn(config));
export type Filter = (config: TransceiverConfig) => boolean;

export const createMainStatsSignals = (): MainStatsSignals => {
    const audioIn = createStatsSignals('audio:inbound');
    const audioOut = createStatsSignals('audio:outbound');

    const videoIn = createStatsSignals('video:inbound');
    const videoOut = createStatsSignals('video:outbound');

    const presoVideoIn = createStatsSignals('preso:video:inbound');
    const presoVideoOut = createStatsSignals('preso:video:outbound');

    return {
        audioIn,
        audioOut,
        videoIn,
        videoOut,
        presoVideoIn,
        presoVideoOut,
        combinedRtcStatsSignal: combine(
            audioIn.onRtcStats,
            audioOut.onRtcStats,
            videoIn.onRtcStats,
            videoOut.onRtcStats,
            presoVideoIn.onRtcStats,
            presoVideoOut.onRtcStats,
        ),
        combinedCallQualitySignal: combine(
            audioIn.onCallQuality,
            audioOut.onCallQuality,
        ),
        combinedCallQualityStatsSignal: combine(
            audioIn.onCallQualityStats,
            audioOut.onCallQualityStats,
        ),
    };
};

type MediaKind = 'audio' | 'video' | 'presentation';

const isSending = {
    audio: isSendingAudio,
    video: isSendingVideo,
    presentation: isSendingPresentation,
};
const isReceiving = {
    audio: isReceivingAudio,
    video: isReceivingVideo,
    presentation: isReceivingPresentation,
};

/**
 * Convert @see {@link ClientCallType} to @see {@link RTCRtpTransceiverDirection}
 */
export const toRTCRtpTransceiverDirection = (
    callType: ClientCallType,
    kind: MediaKind,
): Exclude<RTCRtpTransceiverDirection, 'stopped'> => {
    const send = isSending[kind](callType);
    const recv = isReceiving[kind](callType);

    if (send) {
        if (recv) {
            return 'sendrecv';
        }
        return 'sendonly';
    }
    if (recv) {
        return 'recvonly';
    }
    return 'inactive';
};

/**
 * Get the direction based on the provided track and callType
 */
export const getDirection = (kind: MediaKind, callType: ClientCallType) => {
    return toRTCRtpTransceiverDirection(callType, kind);
};

/**
 * Build the SSO target URL based on the provided query parameters
 * @param queryParameters - The query parameters to be used in the SSO target URL
 * @returns The SSO target URL
 */
export const buildSsoTargetUrl = (queryParameters: {
    conferenceAlias: string;
    displayName?: string | false;
    callTag?: string;
    callType?: ClientCallType;
    isMeetingManager?: boolean;
}) => {
    // Get the base URL of the page
    const baseUrl = new URL(`${document.baseURI}m/`);

    baseUrl.searchParams.append('conference', queryParameters.conferenceAlias);

    // Conditional addition of parameters
    if (queryParameters.displayName) {
        baseUrl.searchParams.append('name', queryParameters.displayName);
    }

    if (queryParameters.callTag) {
        baseUrl.searchParams.append('callTag', queryParameters.callTag);
    }

    if (queryParameters.callType) {
        baseUrl.searchParams.append(
            'callType',
            String(queryParameters.callType),
        );
    }

    if (queryParameters.isMeetingManager) {
        baseUrl.searchParams.append('mm', 'true');
    }

    baseUrl.searchParams.append('ssoToken', '__pexip-sso-token__');

    // SSO redirect URL should trigger a direct join
    baseUrl.searchParams.append('join', '1');

    return baseUrl.toString();
};
